import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API_URL } from "../App";
import { employeeHasRegularDayOff, getDeptBadgeStyle } from "./ScheduleView";

const pad = (n) => String(n).padStart(2, "0");

function HolidayExcused() {
	const [month, setMonth] = useState(() => {
		const now = new Date();
		return new Date(now.getFullYear(), now.getMonth(), 1);
	});
	const [holidays, setHolidays] = useState([]);
	const [employees, setEmployees] = useState([]);
	const [leaves, setLeaves] = useState([]);
	// selections[date] = array of employee ids currently ticked
	const [selections, setSelections] = useState({});
	const [loading, setLoading] = useState(true);
	const [savingDate, setSavingDate] = useState("");
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");

	const year = month.getFullYear();
	const monthNum = month.getMonth() + 1;
	const lastDay = new Date(year, monthNum, 0).getDate();
	const startDate = `${year}-${pad(monthNum)}-01`;
	const endDate = `${year}-${pad(monthNum)}-${pad(lastDay)}`;

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const [holRes, empRes, leaveRes] = await Promise.all([
				axios.get(`${API_URL}/holidays`, {
					params: { startDate, endDate },
				}),
				axios.get(`${API_URL}/employees`),
				axios.get(`${API_URL}/requests`, {
					params: { status: "approved", startDate, endDate },
				}),
			]);
			const hols = [...holRes.data].sort((a, b) =>
				a.holiday_date.localeCompare(b.holiday_date),
			);
			setHolidays(hols);
			setEmployees(empRes.data);
			setLeaves(leaveRes.data);

			const initial = {};
			hols.forEach((h) => {
				initial[h.holiday_date] = leaveRes.data
					.filter(
						(l) =>
							l.request_date === h.holiday_date &&
							String(l.leave_type).toLowerCase() === "holiday",
					)
					.map((l) => l.employee_id);
			});
			setSelections(initial);
			setError("");
		} catch (err) {
			setError("Failed to load holiday data");
		} finally {
			setLoading(false);
		}
	}, [startDate, endDate]);

	useEffect(() => {
		load();
	}, [load]);

	const isHolidayLeave = (l) =>
		String(l.leave_type).toLowerCase() === "holiday";

	const savedIdsFor = (dateStr) =>
		leaves
			.filter((l) => l.request_date === dateStr && isHolidayLeave(l))
			.map((l) => l.employee_id);

	// Available = not their NWD and no other leave that day. Anyone already
	// excused stays listed so they can be un-selected.
	const getAvailableEmployees = (dateStr) => {
		const date = new Date(dateStr + "T00:00:00");
		const saved = new Set(savedIdsFor(dateStr));
		return employees
			.filter((emp) => {
				if (saved.has(emp.id)) return true;
				if (employeeHasRegularDayOff(emp, date)) return false;
				const otherLeave = leaves.some(
					(l) =>
						l.employee_id === emp.id &&
						l.request_date === dateStr &&
						!isHolidayLeave(l),
				);
				return !otherLeave;
			})
			.sort((a, b) => {
				const sup = (b.is_supervisor ? 1 : 0) - (a.is_supervisor ? 1 : 0);
				return sup || (a.first_name || "").localeCompare(b.first_name || "");
			});
	};

	const toggle = (dateStr, empId) => {
		setSelections((prev) => {
			const current = prev[dateStr] || [];
			return {
				...prev,
				[dateStr]: current.includes(empId)
					? current.filter((id) => id !== empId)
					: [...current, empId],
			};
		});
	};

	const setAll = (dateStr, ids) =>
		setSelections((prev) => ({ ...prev, [dateStr]: ids }));

	const isDirty = (dateStr) => {
		const a = [...(selections[dateStr] || [])].sort().join(",");
		const b = [...savedIdsFor(dateStr)].sort().join(",");
		return a !== b;
	};

	const save = async (dateStr) => {
		setSavingDate(dateStr);
		setError("");
		try {
			await axios.post(`${API_URL}/requests/holiday-excused`, {
				date: dateStr,
				employeeIds: selections[dateStr] || [],
			});
			setSuccess("Holiday excused list saved");
			await load();
			setTimeout(() => setSuccess(""), 3000);
		} catch (err) {
			setError(err.response?.data?.error || "Failed to save");
		} finally {
			setSavingDate("");
		}
	};

	const changeMonth = (delta) =>
		setMonth(new Date(year, month.getMonth() + delta, 1));

	const formatDate = (dateStr) =>
		new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
			weekday: "long",
			month: "long",
			day: "numeric",
		});

	return (
		<div>
			<p className='text-muted mb-md'>
				Choose who is excused on each holiday. Only employees who are
				available to work that day are listed (not their NWD and no other
				leave). Select the employees to give Holiday Excused, then save.
			</p>

			{success && <div className='alert alert-success'>{success}</div>}
			{error && <div className='alert alert-error'>{error}</div>}

			<div
				className='card'
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					padding: "1rem 1.5rem",
					marginBottom: "1rem",
				}}
			>
				<button
					className='btn btn-secondary'
					onClick={() => changeMonth(-1)}
				>
					◀ Previous
				</button>
				<h2 style={{ margin: 0, fontSize: "1.25rem" }}>
					{month.toLocaleDateString("en-US", {
						month: "long",
						year: "numeric",
					})}
				</h2>
				<button
					className='btn btn-secondary'
					onClick={() => changeMonth(1)}
				>
					Next ▶
				</button>
			</div>

			{loading ? (
				<div className='loading-container'>
					<div className='spinner'></div>
				</div>
			) : holidays.length === 0 ? (
				<div
					className='card text-center text-muted'
					style={{ padding: "2rem" }}
				>
					No holidays in this month.
				</div>
			) : (
				holidays.map((holiday) => {
					const dateStr = holiday.holiday_date;
					const available = getAvailableEmployees(dateStr);
					const selected = selections[dateStr] || [];
					const dirty = isDirty(dateStr);
					const saving = savingDate === dateStr;
					return (
						<div
							className='card'
							key={dateStr}
							style={{ marginBottom: "1rem" }}
						>
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									flexWrap: "wrap",
									gap: "0.5rem",
									padding: "1rem 1.5rem",
									borderBottom: "1px solid var(--gray-200)",
								}}
							>
								<div>
									<h3 style={{ margin: 0 }}>
										🎉 {holiday.name}
									</h3>
									<span className='text-sm text-muted'>
										{formatDate(dateStr)} · {selected.length}{" "}
										excused of {available.length} available
										· {available.length - selected.length}{" "}
										working
									</span>
								</div>
								<div className='flex gap-sm'>
									<button
										className='btn btn-sm btn-secondary'
										onClick={() =>
											setAll(
												dateStr,
												available.map((e) => e.id),
											)
										}
									>
										Select all
									</button>
									<button
										className='btn btn-sm btn-secondary'
										onClick={() => setAll(dateStr, [])}
									>
										Clear
									</button>
									<button
										className='btn btn-sm btn-primary'
										disabled={!dirty || saving}
										onClick={() => save(dateStr)}
									>
										{saving
											? "Saving..."
											: dirty
												? "Save"
												: "Saved"}
									</button>
								</div>
							</div>

							{available.length === 0 ? (
								<div
									className='text-muted'
									style={{ padding: "1rem 1.5rem" }}
								>
									No employees are available to work this
									day.
								</div>
							) : (
								<div className='table-container'>
									<table className='table'>
										<thead>
											<tr>
												<th style={{ width: "60px" }}>
													Excuse
												</th>
												<th>Employee</th>
												<th>Departments</th>
											</tr>
										</thead>
										<tbody>
											{available.map((emp) => {
												const checked =
													selected.includes(emp.id);
												return (
													<tr
														key={emp.id}
														onClick={() =>
															toggle(
																dateStr,
																emp.id,
															)
														}
														style={{
															cursor: "pointer",
															background: checked
																? "var(--warning-100)"
																: undefined,
														}}
													>
														<td>
															<input
																type='checkbox'
																checked={checked}
																onChange={() =>
																	toggle(
																		dateStr,
																		emp.id,
																	)
																}
																onClick={(e) =>
																	e.stopPropagation()
																}
															/>
														</td>
														<td className='font-medium'>
															{emp.is_supervisor ? (
																<span
																	title='Supervisor'
																	style={{
																		color: "var(--accent)",
																		marginRight:
																			"4px",
																	}}
																>
																	★
																</span>
															) : null}
															{emp.first_name}
														</td>
														<td>
															<div
																style={{
																	display:
																		"flex",
																	flexWrap:
																		"wrap",
																	gap: "4px",
																}}
															>
																{(
																	emp.departments ||
																	[]
																).map((d) => (
																	<span
																		key={d.id}
																		className='badge badge-primary'
																		style={getDeptBadgeStyle(
																			d.name,
																		)}
																	>
																		{d.name}
																	</span>
																))}
															</div>
														</td>
													</tr>
												);
											})}
										</tbody>
									</table>
								</div>
							)}
						</div>
					);
				})
			)}
		</div>
	);
}

export default HolidayExcused;
